import { useEffect, useMemo, useRef, useState } from "react";
import { Copy, Link as LinkIcon, Radio, RefreshCw, UsersRound } from "lucide-react";
import { toast } from "sonner";
import { getCommercialSession } from "../lib/api";
import PageWrapper from "../pages/PageWrapper";

const websocketUrl = (sessionId) => {
  const httpBase = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/$/, "");
  return `${httpBase.replace(/^http/, "ws")}/api/v2/sessions/${sessionId}/ws`;
};

const statusLabel = (status) => {
  if (status === "waiting") return "En attente du deuxième joueur";
  if (status === "ready") return "Lobby prêt";
  if (status === "starting") return "Démarrage synchronisé";
  if (status === "running") return "Partie en cours";
  if (status === "completed") return "Partie terminée";
  return "Lobby privé";
};

const resultProfile = (score) => {
  if (score === 100) return {
    title: "COUPLE GOALS 🏆💘",
    text: "50 manches, zéro faux pas. C’est indécent. Vous venez officiellement de rendre les autres duos un peu moins sûrs d’eux.",
    next: "Rejouez quand même : statistiquement, vous ne pouvez pas être aussi insupportablement parfaits deux fois de suite.",
  };
  if (score >= 90) return {
    title: "Télépathie illégale 🧠⚡",
    text: "À ce niveau-là, soit vous vous connaissez beaucoup trop bien, soit l’un de vous a accès aux réponses de l’autre.",
    next: "Encore une partie pour déterminer s’il s’agit d’amour, de sorcellerie ou d’un excellent Wi-Fi émotionnel.",
  };
  if (score >= 75) return {
    title: "Duo premium ✨",
    text: "Très franchement, ça fonctionne dangereusement bien. Quelques divergences subsistent juste pour conserver un minimum de personnalité.",
    next: "Rejouez : les petits désaccords sont précisément là où ça devient intéressant.",
  };
  if (score >= 60) return {
    title: "Ça matche sévère 😏",
    text: "Beaucoup de points communs, de bonnes intuitions et quelques moments où vous vous demandez qui est cette personne en face.",
    next: "Vous avez largement de quoi continuer. La prochaine partie peut encore faire monter — ou exploser — le score.",
  };
  if (score >= 45) return {
    title: "Compatibles… sous conditions 📜",
    text: "Il y a du potentiel. Il y a aussi quelques réponses qui mériteraient probablement une réunion de crise autour d’un apéro.",
    next: "Une revanche s’impose : ce dossier est beaucoup trop ambigu pour être classé maintenant.",
  };
  if (score >= 30) return {
    title: "Vous aimez le challenge 🔥",
    text: "On ne va pas mentir : vous n’avez pas exactement commandé le même cerveau. Mais au moins, personne ne risque de s’ennuyer.",
    next: "Rejouez. Soit vous apprenez à mieux vous connaître, soit vous confirmez scientifiquement le chaos.",
  };
  if (score >= 15) return {
    title: "Le multivers vous sépare 🌌",
    text: "Vous vivez manifestement dans deux réalités parallèles qui ont accepté, pour une raison encore inconnue, de partager cette partie.",
    next: "Une deuxième tentative est fortement recommandée. Ne serait-ce que pour la science.",
  };
  return {
    title: "NE VOUS REPRODUISEZ PAS ! ☣️😂",
    text: "DualFlip ne délivre aucun avis médical, mais vos réponses viennent de déclencher plusieurs alarmes imaginaires dans nos serveurs.",
    next: "Rejouez immédiatement. Un score pareil mérite soit une revanche historique, soit d’être encadré et accroché au mur.",
  };
};

export default function CommercialSessionLobby({ connection, onCreateAnother }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [socketState, setSocketState] = useState("connecting");
  const [serverOffset, setServerOffset] = useState(0);
  const [clock, setClock] = useState(Date.now());
  const socketRef = useRef(null);

  const inviteUrl = useMemo(() => {
    if (!connection?.inviteCode) return "";
    return `${window.location.origin}/join/${connection.inviteCode}`;
  }, [connection?.inviteCode]);

  const applyRealtimeSession = (nextSession) => {
    if (!nextSession) return;
    if (nextSession.serverTime) {
      setServerOffset(new Date(nextSession.serverTime).getTime() - Date.now());
    }
    setSession(nextSession);
  };

  const refresh = async (manual = false) => {
    if (!connection?.sessionId || !connection?.participantToken) return;
    if (manual) setRefreshing(true);
    try {
      const current = await getCommercialSession(connection.sessionId, connection.participantToken);
      setSession((previous) => {
        if (!previous) return current;
        return {
          ...current,
          participants: current?.participants || previous?.participants,
          gameplay: current?.gameplay || previous?.gameplay,
          serverTime: current?.serverTime || previous?.serverTime,
          startDeadlineAt: current?.startDeadlineAt ?? previous?.startDeadlineAt,
          startedAt: current?.startedAt ?? previous?.startedAt,
        };
      });
      if (manual) toast.success("Lobby actualisé");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Impossible d'actualiser le lobby");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    refresh();
  }, [connection?.sessionId, connection?.participantToken]);

  useEffect(() => {
    if (!connection?.sessionId || !connection?.participantId || !connection?.participantToken) return undefined;
    let disposed = false;
    let reconnectTimer = null;
    let heartbeatTimer = null;
    let socket = null;

    const connect = () => {
      if (disposed) return;
      setSocketState("connecting");
      socket = new WebSocket(websocketUrl(connection.sessionId));
      socketRef.current = socket;

      socket.onopen = () => {
        if (disposed) return;
        socket.send(JSON.stringify({
          type: "authenticate",
          participantId: connection.participantId,
          participantToken: connection.participantToken,
        }));
        heartbeatTimer = window.setInterval(() => {
          if (socket.readyState === WebSocket.OPEN) socket.send(JSON.stringify({ type: "heartbeat" }));
        }, 20000);
      };

      socket.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          if (message.session) {
            applyRealtimeSession(message.session);
            setLoading(false);
            setSocketState("connected");
          } else if (message.type === "error") {
            toast.error("Événement de partie non pris en charge");
          }
        } catch {
          toast.error("Mise à jour temps réel illisible");
        }
      };

      socket.onclose = () => {
        window.clearInterval(heartbeatTimer);
        if (disposed) return;
        setSocketState("reconnecting");
        reconnectTimer = window.setTimeout(connect, 1800);
      };
    };

    connect();
    return () => {
      disposed = true;
      window.clearTimeout(reconnectTimer);
      window.clearInterval(heartbeatTimer);
      socket?.close();
    };
  }, [connection?.sessionId, connection?.participantId, connection?.participantToken]);

  useEffect(() => {
    if (session?.status !== "starting" && session?.status !== "running") return undefined;
    const interval = window.setInterval(() => setClock(Date.now()), 100);
    return () => window.clearInterval(interval);
  }, [session?.status]);

  const sendRealtime = (payload) => {
    if (socketRef.current?.readyState !== WebSocket.OPEN) {
      toast.error("Connexion temps réel en cours de rétablissement");
      return false;
    }
    socketRef.current.send(JSON.stringify(payload));
    return true;
  };

  const copyInvite = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      toast.success("Lien d'invitation copié");
    } catch {
      toast.error("Copiez le lien manuellement");
    }
  };

  const players = session?.participants || [session?.participant, session?.otherParticipant].filter(Boolean);
  const own = players.find((player) => player.participantId === connection?.participantId) || session?.participant;
  const other = players.find((player) => player.participantId !== connection?.participantId);
  const secondPlayer = players.find((player) => player.slot === 2);
  const firstPlayer = players.find((player) => player.slot === 1);
  const isWaiting = session?.status === "waiting";
  const isStarting = session?.status === "starting";
  const isRunning = session?.status === "running";
  const isCompleted = session?.status === "completed";
  const gameplay = session?.gameplay;
  const currentRound = gameplay?.currentRound;
  const roundType = currentRound?.type || "classic";
  const isClassic = roundType === "classic";
  const isPrediction = roundType === "prediction";
  const isScale = currentRound?.variant === "scale";
  const phase = gameplay?.phase;
  const answeredIds = gameplay?.answeredParticipantIds || [];
  const predictionIds = gameplay?.predictionParticipantIds || [];
  const ownAnswered = answeredIds.includes(connection?.participantId);
  const otherAnswered = other ? answeredIds.includes(other.participantId) : false;
  const ownPredicted = predictionIds.includes(connection?.participantId);
  const answers = gameplay?.answers || {};
  const predictions = gameplay?.predictions || {};
  const targetSlot = currentRound?.targetSlot;
  const targetPlayer = players.find((player) => player.participantId === gameplay?.targetParticipantId)
    || players.find((player) => player.slot === targetSlot);
  const guesserPlayer = players.find((player) => player.participantId === gameplay?.guesserParticipantId)
    || players.find((player) => targetSlot && player.slot !== targetSlot);
  const isTarget = Boolean(
    (gameplay?.targetParticipantId && gameplay.targetParticipantId === connection?.participantId)
    || (targetSlot && own?.slot === targetSlot)
  );
  const isGuesser = Boolean(
    (gameplay?.guesserParticipantId && gameplay.guesserParticipantId === connection?.participantId)
    || (targetSlot && own?.slot && own.slot !== targetSlot)
  );
  const totalRounds = gameplay?.totalRounds || 50;
  const commonMatches = gameplay?.commonMatches || 0;
  const predictionHits = gameplay?.predictionHits || 0;
  const predictionAttempts = gameplay?.predictionAttempts || 0;
  const successCount = commonMatches + predictionHits;
  const affinityScore = Math.round((successCount / Math.max(1, totalRounds)) * 100);
  const profile = resultProfile(affinityScore);

  const countdownSeconds = isStarting && session?.startDeadlineAt
    ? Math.max(0, Math.ceil((new Date(session.startDeadlineAt).getTime() - (clock + serverOffset)) / 1000))
    : null;
  const roundSeconds = isRunning && gameplay?.roundDeadlineAt && phase !== "reveal"
    ? Math.max(0, Math.ceil((new Date(gameplay.roundDeadlineAt).getTime() - (clock + serverOffset)) / 1000))
    : null;

  const optionDisplayLabel = (option) => {
    if (currentRound?.variant !== "which_of_us") return option.label;
    if (option.value === "slot1") return firstPlayer?.pseudonym || option.label;
    if (option.value === "slot2") return secondPlayer?.pseudonym || option.label;
    return option.label;
  };

  const optionLabel = (value) => {
    const option = currentRound?.options?.find((item) => item.value === value);
    return option ? optionDisplayLabel(option) : "Pas de réponse";
  };

  const optionButtons = (onSelect, disabled) => (
    <div className="answer-row">
      {(currentRound?.options || []).map((option) => (
        <button
          key={option.value}
          type="button"
          className={`answer-btn answer-${option.value === "yes" ? "yes" : option.value === "maybe" ? "maybe" : "no"}`}
          onClick={() => onSelect(option.value)}
          disabled={disabled || socketState !== "connected"}
        >
          <span className="answer-emoji">{option.emoji}</span>
          <span className="answer-label">{optionDisplayLabel(option)}</span>
        </button>
      ))}
    </div>
  );

  const playerStatus = (player) => {
    if (!player) return "Invitation ouverte";
    if (player.online) return player.participantId === connection?.participantId || player.slot === own?.slot
      ? "Vous êtes connecté·e"
      : `${player.pseudonym} est connecté·e`;
    return player.participantId === connection?.participantId || player.slot === own?.slot
      ? "Connexion en cours…"
      : `${player.pseudonym} s’est déconnecté·e…`;
  };

  return (
    <PageWrapper>
      <div className="commercial-lobby" data-testid="commercial-lobby">
        <p className="page-eyebrow">Dualflip · Partie privée</p>
        <h1 className="page-title">Votre lobby</h1>

        {loading ? (
          <div className="center-note">Connexion au lobby…</div>
        ) : isCompleted ? (
          <section className="commercial-running-card" data-testid="commercial-session-completed">
            <span>DUALFLIP · VERDICT</span>
            <h2>{profile.title}</h2>
            <p>{profile.text}</p>

            <div className="stat-grid">
              <div className="stat-card">
                <strong className="stat-value">{affinityScore}%</strong>
                <span className="stat-label">Synchronisation</span>
              </div>
              <div className="stat-card">
                <strong className="stat-value">{commonMatches}</strong>
                <span className="stat-label">Réponses identiques</span>
              </div>
              <div className="stat-card">
                <strong className="stat-value">{predictionHits}/{predictionAttempts || 6}</strong>
                <span className="stat-label">Prédictions réussies</span>
              </div>
            </div>

            <p className="page-note" style={{ marginTop: 20 }}>{profile.next}</p>

            {onCreateAnother && (
              <button className="btn-primary" type="button" onClick={onCreateAnother}>On remet ça ? 😏</button>
            )}
          </section>
        ) : isRunning ? (
          <section className="commercial-running-card" data-testid="commercial-session-running">
            <div className="cards-progress">Manche {(gameplay?.roundIndex ?? 0) + 1} / {totalRounds}</div>
            <span>{currentRound?.category || "DUALFLIP"}</span>
            <h2>{currentRound?.text || "Préparation de la manche…"}</h2>

            {isClassic && phase === "answering" && (
              <>
                <p><strong>{roundSeconds}s</strong> · même chrono pour les deux joueurs</p>
                {isScale && currentRound?.scaleLabels?.length === 2 && (
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 16, margin: "8px 0 12px" }}>
                    <small>{currentRound.scaleLabels[0]}</small>
                    <small>{currentRound.scaleLabels[1]}</small>
                  </div>
                )}
                {optionButtons((answer) => sendRealtime({ type: "answer_round", answer }), ownAnswered)}
                <p>
                  {ownAnswered ? "Réponse verrouillée ✓" : isScale ? "Placez votre curseur de 1 à 5." : "Choisissez votre réponse."}
                  {otherAnswered ? ` · ${other?.pseudonym || "L'autre joueur"} a répondu ✓` : ""}
                </p>
              </>
            )}

            {isPrediction && (phase === "target_answer" || phase === "answering") && (
              isTarget ? (
                <>
                  <p><strong>{roundSeconds}s</strong> · répondez sans montrer votre choix</p>
                  <p>{guesserPlayer?.pseudonym || "L'autre joueur"} devra ensuite deviner votre réponse.</p>
                  {optionButtons((answer) => sendRealtime({ type: "answer_round", answer }), ownAnswered)}
                  <p>{ownAnswered ? "Réponse secrète verrouillée ✓" : "À vous de répondre."}</p>
                </>
              ) : (
                <div className="reveal-prompt">
                  <div className="reveal-prompt-glow" />
                  <h2>{targetPlayer?.pseudonym || "L'autre joueur"} répond…</h2>
                  <p>Sa réponse reste secrète. Dans un instant, à vous de la deviner.</p>
                  <p><strong>{roundSeconds}s</strong></p>
                </div>
              )
            )}

            {isPrediction && phase === "predicting" && (
              isGuesser ? (
                <>
                  <p><strong>{roundSeconds}s</strong> · que pensez-vous que {targetPlayer?.pseudonym || "l'autre joueur"} a répondu ?</p>
                  {optionButtons((predictionValue) => sendRealtime({ type: "predict_round", prediction: predictionValue }), ownPredicted)}
                  <p>{ownPredicted ? "Prédiction verrouillée ✓" : "Fiez-vous à ce que vous connaissez de l'autre."}</p>
                </>
              ) : (
                <div className="reveal-prompt">
                  <div className="reveal-prompt-glow" />
                  <h2>Réponse enregistrée 🔒</h2>
                  <p>{guesserPlayer?.pseudonym || "L'autre joueur"} essaie maintenant de deviner votre choix.</p>
                  <p><strong>{roundSeconds}s</strong></p>
                </div>
              )
            )}

            {phase === "reveal" && isClassic && (
              <div className="reveal-prompt">
                <div className="reveal-prompt-glow" />
                <h2>{isScale ? (gameplay?.matched ? "Même curseur ! 🎚️" : "Vos curseurs") : (gameplay?.matched ? "Point commun ! ✨" : "Révélation")}</h2>
                <p><strong>Vous :</strong> {optionLabel(answers[connection?.participantId])}</p>
                <p><strong>{other?.pseudonym || "L'autre joueur"} :</strong> {optionLabel(answers[other?.participantId])}</p>
                <p>{isScale ? (gameplay?.matched ? "Vous vous situez exactement au même niveau." : "Deux niveaux différents : intéressant à comparer à deux.") : (gameplay?.matched ? "Vous avez choisi la même réponse." : "Deux réponses différentes — et c'est aussi ce qui rend le jeu intéressant.")}</p>
              </div>
            )}

            {phase === "reveal" && isPrediction && (
              <div className="reveal-prompt">
                <div className="reveal-prompt-glow" />
                <h2>{gameplay?.predictionCorrect ? "Bien vu ! 🎯" : "Raté… cette fois 😏"}</h2>
                <p><strong>{targetPlayer?.pseudonym || "Réponse"} :</strong> {optionLabel(answers[gameplay?.targetParticipantId || targetPlayer?.participantId])}</p>
                <p><strong>Prédiction de {guesserPlayer?.pseudonym || "l'autre"} :</strong> {optionLabel(predictions[gameplay?.guesserParticipantId || guesserPlayer?.participantId])}</p>
                <p>{gameplay?.predictionCorrect ? "Vous aviez parfaitement anticipé sa réponse." : "Vous venez d'apprendre quelque chose de plus sur l'autre."}</p>
              </div>
            )}
          </section>
        ) : (
          <>
            {isStarting && (
              <section className="commercial-countdown-card" data-testid="commercial-countdown">
                <span className="commercial-countdown-kicker">Démarrage synchronisé</span>
                <strong>{countdownSeconds > 0 ? countdownSeconds : "DUALFLIP"}</strong>
                <p>Le compte à rebours suit la référence horaire du serveur.</p>
              </section>
            )}

            <section className="commercial-code-card">
              <span className="commercial-status">{statusLabel(session?.status)}</span>
              <strong className="commercial-code" data-testid="invite-code">{session?.inviteCode || connection?.inviteCode}</strong>
              <p>Partagez ce code ou ce lien avec votre second joueur.</p>
              <div className="commercial-share-link">
                <LinkIcon size={16} aria-hidden="true" />
                <span>{inviteUrl}</span>
              </div>
              <button className="commercial-secondary-btn" type="button" onClick={copyInvite}>
                <Copy size={17} aria-hidden="true" /> Copier le lien
              </button>
            </section>

            <section className="commercial-players-card">
              <div className="commercial-players-heading">
                <UsersRound size={20} aria-hidden="true" />
                <div>
                  <strong>Joueurs de cette partie</strong>
                  <span>{players.length} / 2 participants · {socketState === "connected" ? "temps réel actif" : "reconnexion…"}</span>
                </div>
              </div>
              {[firstPlayer, secondPlayer].map((player, index) => (
                <div className="commercial-player-row" key={player?.slot || index + 1}>
                  <span className="commercial-slot">{index + 1}</span>
                  <strong>{player?.pseudonym || "En attente…"}</strong>
                  <em className={player?.online ? "is-online" : ""}>{playerStatus(player)}</em>
                </div>
              ))}
            </section>

            {!isWaiting && !isStarting && (
              <section className="commercial-ready-panel">
                <div>
                  <Radio size={19} aria-hidden="true" />
                  <span>
                    <strong>{own?.ready ? "Vous êtes prêt·e" : "Prêt·e à démarrer ?"}</strong>
                    <small>Le serveur lancera le compte à rebours lorsque vous serez prêts tous les deux et connectés.</small>
                  </span>
                </div>
                <button
                  className="btn-primary"
                  type="button"
                  onClick={() => sendRealtime({ type: "set_ready", ready: true })}
                  disabled={Boolean(own?.ready) || socketState !== "connected"}
                  data-testid="commercial-ready-button"
                >
                  {own?.ready ? "En attente de l'autre joueur" : "Je suis prêt·e"}
                </button>
              </section>
            )}

            <button className="commercial-refresh-btn" type="button" onClick={() => refresh(true)} disabled={refreshing}>
              <RefreshCw size={17} className={refreshing ? "is-spinning" : ""} aria-hidden="true" />
              {refreshing ? "Actualisation…" : "Actualiser le lobby"}
            </button>
          </>
        )}
      </div>
    </PageWrapper>
  );
}
